import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listWorkflowCategories } from '../../api/workflow';
import Spinner from '../ui/Spinner';

// "The same field" across workflows: same step, same registration link (or, unlinked, same label).
const groupOf = (step, field) => `${step.step_definition_id}|${field.linked_registration_field || field.label.trim().toLowerCase()}`;
// Built-in registration fields (not stored as step fields); picked as "fixed:<key>" on the registration step.
const FIXED_FIELDS = [
  ['full_name', 'Full Name'],
  ['contact_no', 'Contact No'],
  ['whatsapp_no', 'WhatsApp No'],
  ['home_country_contact_no', 'Home Country / Emergency Contact No'],
  ['email', 'Email'],
  ['nationality', 'Nationality'],
  ['date_of_birth', 'Date of Birth'],
  ['passport_no', 'Passport No'],
  ['passport_expiry_date', 'Passport Expiry Date'],
  ['old_emirates_id_no', 'Old Emirates ID No'],
  ['old_visa', 'Old Visa'],
  ['visa_type', 'Visa Type'],
  ['visa_validity', 'Visa Validity'],
  ['uid_no', 'UID No'],
  ['home_license_no', 'Home Country DL No'],
  ['home_license_expiry', 'Home Country DL Expiry'],
  ['uae_license_no', 'UAE DL No'],
  ['uae_license_expiry', 'UAE DL Expiry'],
].map(([key, label]) => ({ id: `fixed:${key}`, label, linked_registration_field: key }));

const stepFields = (step) => [...(step.code === 'registration' ? FIXED_FIELDS : []), ...(step.fields || [])];

const pickId = (stepId, fieldId) => `${stepId}:${fieldId}`;

/**
 * Pick, across every workflow, the step fields to show beside a document while it is verified.
 * Ticking a field also ticks the same field of the same step in the other
 * workflows, since the lead may belong to any of them.
 *
 * value: [{ workflow_step_id, field_id }]
 */
export default function DocumentFieldPicker({ value, onChange }) {
  const [openId, setOpenId] = useState(null);
  const query = useQuery({ queryKey: ['workflow-categories'], queryFn: listWorkflowCategories });

  const picked = useMemo(() => new Set(value.map((p) => pickId(p.workflow_step_id, p.field_id))), [value]);

  // Fields that count as "the same" across workflows: same step definition and same registration link.
  const siblings = useMemo(() => {
    const map = new Map();
    (query.data || []).forEach((c) =>
      c.steps.forEach((s) =>
        stepFields(s).forEach((f) => {
          const group = groupOf(s, f);
          map.set(group, [...(map.get(group) || []), { workflow_step_id: s.id, field_id: f.id }]);
        })
      )
    );
    return map;
  }, [query.data]);

  const toggle = (step, field) => {
    const group = siblings.get(groupOf(step, field)) || [{ workflow_step_id: step.id, field_id: field.id }];
    const on = picked.has(pickId(step.id, field.id));
    const ids = new Set(group.map((p) => pickId(p.workflow_step_id, p.field_id)));
    const kept = value.filter((p) => !ids.has(pickId(p.workflow_step_id, p.field_id)));
    onChange(on ? kept : [...kept, ...group]);
  };

  if (query.isLoading) return <Spinner className="mx-auto my-4" />;

  return (
    <div>
      <div className="mb-1 text-sm font-medium text-gray-700">Fields to cross-check ({value.length} selected)</div>
      <p className="mb-2 text-xs text-gray-500">
        Shown beside this document when it is verified. Values come from the lead where it has them (linked or same-named fields); the rest are optional checks.
      </p>
      <div className="max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
        {(query.data || []).map((category) => {
          const count = category.steps.reduce((n, s) => n + stepFields(s).filter((f) => picked.has(pickId(s.id, f.id))).length, 0);
          const open = openId === category.id;
          return (
            <div key={category.id}>
              <button
                type="button"
                onClick={() => setOpenId(open ? null : category.id)}
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-gray-800 hover:bg-gray-50"
              >
                <span>{category.name}</span>
                <span className="text-xs text-gray-500">
                  {count > 0 ? `${count} selected · ` : ''}
                  {open ? '−' : '+'}
                </span>
              </button>
              {open &&
                category.steps
                  .filter((s) => stepFields(s).length > 0)
                  .map((step) => (
                    <div key={step.id} className="bg-gray-50 px-3 py-2">
                      <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">{step.title}</div>
                      {stepFields(step).map((field) => (
                        <label key={field.id} className="flex cursor-pointer items-center gap-2 py-0.5 text-sm text-gray-800">
                          <input
                            type="checkbox"
                            checked={picked.has(pickId(step.id, field.id))}
                            onChange={() => toggle(step, field)}
                            className="h-4 w-4 rounded border-gray-300 text-primary"
                          />
                          {field.label}
                        </label>
                      ))}
                    </div>
                  ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
